import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { format, isValid } from 'date-fns';
import { TransactionStatus, TransactionHistoryItem } from '../../Utility/VoPayApi/VoPayApi.types';
import Box from '../../Layout/Box/Box';
import Title2 from '../../Text/Title/Title2';
import CircleButton from '../../Input/CircleButton/CircleButton';
import StatusIcon from './StatusIcon/StatusIcon';
import { withStyles } from '../../../utils/utils';
import styles from './TransactionStatusStepper.module.scss';

export type TransactionStatusStepperProps = {
  transactionHistory: TransactionHistoryItem[];
  failureReason?: string;
  // Replaces the default Waiting message on the final step only — callers with extra context
  // (e.g. a funding parent, an eLinx-originated payment) can say exactly what it's waiting on.
  waitingMessageOverride?: string;
};

const FAILED_STATUSES = [TransactionStatus.FAILED, TransactionStatus.CANCELLED, TransactionStatus.DECLINED];
const FINAL_STATUSES = [
  TransactionStatus.SUCCESSFUL,
  TransactionStatus.COMPLETE,
  TransactionStatus.FAILED,
  TransactionStatus.CANCELLED,
  TransactionStatus.SETTLED,
  TransactionStatus.DECLINED,
];

const STATUS_MESSAGE_DEFAULTS: Record<TransactionStatus, string> = {
  [TransactionStatus.REQUESTED]: 'This transaction request has been requested.',
  [TransactionStatus.PENDING]: 'This transaction is pending.',
  [TransactionStatus.IN_PROGRESS]: 'This transaction is in progress.',
  [TransactionStatus.SENT]: 'This transaction has been sent.',
  [TransactionStatus.SUCCESSFUL]: 'Transaction completed successfully.',
  [TransactionStatus.COMPLETE]: 'Transaction completed successfully.',
  [TransactionStatus.SETTLED]: 'This transaction has been settled.',
  [TransactionStatus.FAILED]: 'This transaction has been failed.',
  [TransactionStatus.CANCELLED]: 'This transaction has been cancelled.',
  [TransactionStatus.PENDING_APPROVAL]: 'This transaction is awaiting approval.',
  [TransactionStatus.WAITING]: 'This transaction is waiting.',
  [TransactionStatus.CANCELLATION_REQUESTED]: 'Cancellation has been requested.',
  [TransactionStatus.BLOCKED_BY_FINANCIAL_INSTITUTION]: 'This transaction was blocked by financial institution.',
  [TransactionStatus.PENDING_CONFIRMATION]: 'This transaction is waiting for confirmation.',
  [TransactionStatus.DECLINED]: 'This transaction has been declined.',
  [TransactionStatus.RECEIVED]: 'This transaction has been received.',
  [TransactionStatus.BLANK]: 'Unknown status.',
};

const STATUS_KEYS: Record<TransactionStatus, string> = {
  [TransactionStatus.REQUESTED]: 'requested',
  [TransactionStatus.PENDING]: 'pending',
  [TransactionStatus.IN_PROGRESS]: 'inProgress',
  [TransactionStatus.SENT]: 'sent',
  [TransactionStatus.SUCCESSFUL]: 'successful',
  [TransactionStatus.COMPLETE]: 'complete',
  [TransactionStatus.SETTLED]: 'settled',
  [TransactionStatus.FAILED]: 'failed',
  [TransactionStatus.CANCELLED]: 'cancelled',
  [TransactionStatus.CANCELLATION_REQUESTED]: 'cancellationRequested',
  [TransactionStatus.PENDING_APPROVAL]: 'pendingApproval',
  [TransactionStatus.PENDING_CONFIRMATION]: 'pendingConfirmation',
  [TransactionStatus.WAITING]: 'waiting',
  [TransactionStatus.BLOCKED_BY_FINANCIAL_INSTITUTION]: 'blocked',
  [TransactionStatus.DECLINED]: 'declined',
  [TransactionStatus.RECEIVED]: 'received',
  [TransactionStatus.BLANK]: 'unknown',
};

function getStatusMessageKey(status: TransactionStatus): string {
  return status === TransactionStatus.SUCCESSFUL ? STATUS_KEYS[TransactionStatus.COMPLETE] : STATUS_KEYS[status];
}

const cn = withStyles(styles);

export default function TransactionStatusStepper({
  transactionHistory,
  failureReason = '',
  waitingMessageOverride,
}: TransactionStatusStepperProps) {
  const { t } = useTranslation('components', { keyPrefix: 'TransactionStatusStepper' });

  const containerRef = useRef<HTMLDivElement>(null);
  const stepsRef = useRef<HTMLDivElement>(null);
  const [hasOverflow, setHasOverflow] = useState(false);

  const STEP = 50;
  const [offset, setOffset] = useState(0);
  const [maxOffset, setMaxOffset] = useState(0);

  const handleDown = () => {
    setOffset((prev) => Math.min(prev + STEP, maxOffset));
  };

  const handleUp = () => {
    setOffset((prev) => Math.max(prev - STEP, 0));
  };

  useEffect(() => {
    if (!containerRef.current || !stepsRef.current) return;

    const containerHeight = containerRef.current.offsetHeight;
    const stepsHeight = stepsRef.current.offsetHeight;
    const overflow = stepsHeight > containerHeight;

    const calculatedMaxOffset = Math.max(0, stepsHeight - containerHeight);

    setHasOverflow(overflow);
    setMaxOffset(calculatedMaxOffset);

    setOffset(calculatedMaxOffset);
  }, [transactionHistory]);

  if (transactionHistory.length === 0) return null;

  return (
    <div className={cn('statusWrapper')}>
      <div className={cn('statusContainer')} ref={containerRef}>
        <div className={cn(hasOverflow && 'statusSteps')} ref={stepsRef} style={hasOverflow ? { top: -offset } : {}}>
          {transactionHistory.map((item, index) => {
            return (
              <div
                className={cn('status', !isFinalStep(index) && 'border', isFailed(item.Status) ? 'red' : 'green')}
                key={`${item.Status}-${item.DateAdded}`}
              >
                <div className={cn('icon', !isFinalStep(index) && 'small')}>
                  <StatusIcon
                    status={getStatusIcon(item.Status, index)}
                    size={isFinalStep(index) ? 'medium' : 'small'}
                  />
                </div>

                <Box flx="col" gapSize="4px">
                  <Title2 level={isFinalStep(index) ? '4' : '5'} className={cn('capitalize')}>
                    {t(`Statuses.${STATUS_KEYS[item.Status]}`, { defaultValue: item.Status })}
                  </Title2>
                  {transactionHistory.length === index + 1 && (
                    <Title2 level="6" emphasized={false} className={cn('subTitle', 'grey')}>
                      {item.Status === TransactionStatus.FAILED && failureReason
                        ? failureReason
                        : item.Status === TransactionStatus.WAITING && waitingMessageOverride
                          ? waitingMessageOverride
                          : t(`StatusMessage.${getStatusMessageKey(item.Status)}`, {
                              defaultValue: STATUS_MESSAGE_DEFAULTS[item.Status],
                            })}
                    </Title2>
                  )}

                  <div className={cn('text')}>
                    {isValid(new Date(item.DateAdded)) ? format(item.DateAdded, 'dd MMM yyyy, HH:mm:ss') : '-'}
                  </div>
                </Box>
              </div>
            );
          })}
        </div>
      </div>
      {hasOverflow && offset > 0 && (
        <div className={cn('fadeOverlay', 'top', 'hiddenFormsPrint')}>
          <CircleButton
            icon="keyboard_arrow_up"
            size="large"
            circleFill
            className={cn('arrowButton', 'up')}
            onClick={handleUp}
          />
        </div>
      )}

      {hasOverflow && offset < maxOffset && (
        <div className={cn('fadeOverlay', 'bottom', 'hiddenFormsPrint')}>
          <CircleButton
            icon="keyboard_arrow_down"
            size="large"
            circleFill
            className={cn('arrowButton', 'down')}
            onClick={handleDown}
          />
        </div>
      )}
    </div>
  );

  function isFinalStep(index: number) {
    return transactionHistory.length === index + 1 || false;
  }

  function isFailed(status: TransactionStatus) {
    return FAILED_STATUSES.includes(status);
  }

  function getStatusIcon(status: TransactionStatus, index: number): 'pending' | 'successful' | 'failed' {
    if (isFailed(status)) return 'failed';
    if (isFinalStep(index) && !FINAL_STATUSES.includes(status)) return 'pending';

    return 'successful';
  }
}
